import { Fragment, type ReactNode } from 'react'
import { LocalizedLink as Link } from '@/components/LocalizedLink'
import { useTar } from '@/modules'
import { trackMarketing, type GuideId } from '@/modules/marketingAnalytics'

export function GuideLastUpdated({ isoDate }: { isoDate: string }) {
  const { t, locale } = useTar()
  const [year, month, day] = isoDate.split('-').map(Number)
  const formatted = new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
    new Date(year, month - 1, day)
  )
  return (
    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
      <time dateTime={isoDate}>
        {t('intentResources.lastUpdated')} · {formatted}
      </time>
    </p>
  )
}

type IntentRelatedLink =
  | { to: string; href?: never; label: string }
  | { href: string; to?: never; label: string }

export function IntentRelatedLinks({
  links,
  source,
}: {
  links: readonly IntentRelatedLink[]
  source: GuideId
}) {
  const { t } = useTar()

  return (
    <nav
      aria-label={t('intentResources.relatedResourcesTitle')}
      className="rounded-xl border border-border bg-card/50 px-6 py-4"
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
        <span className="font-semibold text-foreground">
          {t('intentResources.continueWith')}:
        </span>
        {links.map((link, i) => {
          const destination = 'to' in link && link.to ? link.to : link.href
          const handleClick = () =>
            trackMarketing('guide:related_link:click', {
              guide: source,
              destination,
              label: link.label,
            })
          return (
            <Fragment key={destination}>
              {i > 0 ? <span aria-hidden className="opacity-40">·</span> : null}
              {'to' in link && link.to ? (
                <Link
                  to={link.to}
                  onClick={handleClick}
                  className="text-accent underline-offset-4 transition-colors hover:text-foreground hover:underline"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  href={link.href}
                  onClick={handleClick}
                  className="text-accent underline-offset-4 transition-colors hover:text-foreground hover:underline"
                >
                  {link.label}
                </a>
              )}
            </Fragment>
          )
        })}
      </p>
    </nav>
  )
}

